/**
 * Set Builder — Custom element for product set pages.
 * Renders complementary products as checkbox rows with a combined "Add to Cart" button.
 */

if (!customElements.get('set-builder')) {
  class SetBuilder extends HTMLElement {
    constructor() {
      super();
      this.mainVariantId = this.dataset.mainVariantId;
      this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
      this.abortController = null;
    }

    connectedCallback() {
      // Products are server-rendered from metafield — init directly
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._closeAllSelects);
    }

    init() {
      this.checkboxes = this.querySelectorAll('[data-set-checkbox]');
      this.totalEl = this.querySelector('[data-set-total]');
      this.addBtn = this.querySelector('[data-set-add-to-cart]');
      this.items = this.querySelectorAll('[data-set-item]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      if (!this.addBtn) return;

      // Parse variant data per item
      this.variantsMap = {};
      this.items.forEach((item) => {
        const script = item.querySelector('script[data-product-variants]');
        if (script) {
          try {
            this.variantsMap[item.dataset.productId] = JSON.parse(script.textContent);
          } catch (e) { /* skip */ }
        }
      });

      // Bind checkboxes
      this.checkboxes.forEach((cb) => {
        cb.addEventListener('change', () => this.updateTotal());
      });

      // Init custom selects
      this.initCustomSelects();

      // Add to cart
      this.addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      // Prevent title link clicks from toggling checkbox
      this.querySelectorAll('.set-builder__item-title').forEach((link) => {
        link.addEventListener('click', (e) => e.stopPropagation());
      });

      this.updateTotal();
    }

    /* ========== Custom Select Dropdowns ========== */
    initCustomSelects() {
      this.querySelectorAll('.set-builder__custom-select').forEach((wrapper) => {
        const trigger = wrapper.querySelector('.set-builder__select-trigger');
        const options = wrapper.querySelectorAll('.set-builder__select-option');
        const hiddenSelect = wrapper.closest('.set-builder__option')?.querySelector('select[data-set-native-select]');

        // Toggle dropdown
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const wasOpen = wrapper.classList.contains('is-open');
          this.closeAllSelects();
          if (!wasOpen) wrapper.classList.add('is-open');
        });

        trigger.addEventListener('mousedown', (e) => e.stopPropagation());

        // Option click
        options.forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const val = opt.dataset.value;
            trigger.querySelector('.set-builder__select-value').textContent = val;
            options.forEach((o) => o.classList.remove('is-selected'));
            opt.classList.add('is-selected');
            wrapper.classList.remove('is-open');

            // Sync hidden select and fire change
            if (hiddenSelect) {
              hiddenSelect.value = val;
              hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      });

      // Close on outside click
      this._closeAllSelects = () => this.closeAllSelects();
      document.addEventListener('click', this._closeAllSelects);

      // Bind native select change for variant logic
      this.querySelectorAll('select[data-set-native-select]').forEach((sel) => {
        sel.addEventListener('change', (e) => this.onOptionChange(e));
      });
    }

    closeAllSelects() {
      this.querySelectorAll('.set-builder__custom-select.is-open').forEach((w) => {
        w.classList.remove('is-open');
      });
    }

    /* ========== Variant Selection ========== */
    onOptionChange(event) {
      const select = event.target;
      const item = select.closest('[data-set-item]');
      const productId = item?.dataset.productId;
      if (!item || !productId) return;

      const variants = this.variantsMap[productId];
      if (!variants || !variants.length) return;

      // Gather ALL selected option values for this product
      const selects = item.querySelectorAll('select[data-set-native-select]');
      const selected = Array.from(selects).map((s) => s.value);

      // Find matching variant by comparing each option
      const match = variants.find((v) => {
        return v.options.every((opt, i) => {
          // If fewer selects than options (single-value options hidden), skip comparison
          if (i >= selected.length) return true;
          return opt === selected[i];
        });
      });

      // Fallback: match by the changed option only (for products with hidden single-value options)
      const fallbackMatch = !match ? variants.find((v) => {
        const idx = parseInt(select.dataset.optionIndex, 10);
        return v.options[idx] === select.value;
      }) : null;

      const variant = match || fallbackMatch;

      if (variant) {
        item.dataset.variantId = variant.id;
        item.dataset.price = variant.price;

        const priceEl = item.querySelector('.set-builder__item-price');
        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

        if (variant.featured_image?.src) {
          const img = item.querySelector('.set-builder__img');
          if (img) {
            img.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_160x$1');
          }
        }
        this.updateTotal();
      }
    }

    /* ========== Total Calculation ========== */
    getCheckedItems() {
      const checked = [];
      this.items.forEach((item) => {
        const cb = item.querySelector('[data-set-checkbox]');
        if (cb?.checked) {
          checked.push({
            id: parseInt(item.dataset.variantId, 10),
            price: parseInt(item.dataset.price, 10) || 0,
          });
        }
      });
      return checked;
    }

    updateTotal() {
      const checkedItems = this.getCheckedItems();
      const hasChecked = checkedItems.length > 0;
      const total = checkedItems.reduce((sum, i) => sum + i.price, 0);

      if (this.totalEl) {
        this.totalEl.textContent = hasChecked ? this.formatMoney(total) : '';
      }

      // Update total label
      const labelEl = this.querySelector('[data-set-total-label]');
      if (labelEl) {
        labelEl.textContent = hasChecked ? 'Total:' : 'Select items to complete your look';
      }

      // Toggle button state
      if (this.addBtn) {
        const btnText = this.addBtn.querySelector('.set-builder__btn-text');
        if (hasChecked) {
          this.addBtn.disabled = false;
          if (btnText) btnText.textContent = 'Add to cart';
        } else {
          this.addBtn.disabled = true;
          if (btnText) btnText.textContent = 'Select items to add';
        }
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    /* ========== Add to Cart (Dawn FormData pattern) ========== */
    async addToCart() {
      if (this.addBtn.classList.contains('loading')) return;

      const checkedItems = this.getCheckedItems();
      // Only add checked complementary items (main product has its own buy button)
      const allItems = checkedItems.map((i) => ({ id: i.id, quantity: 1 }));

      this.addBtn.classList.add('loading');
      this.addBtn.disabled = true;

      try {
        // Set active element BEFORE fetch (prevents trapFocus null error)
        if (this.cart && typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }

        // Add items sequentially using FormData (Dawn pattern)
        // First item uses sections param to get cart drawer HTML
        let lastResponse = null;

        for (let i = 0; i < allItems.length; i++) {
          const formData = new FormData();
          formData.append('id', allItems[i].id);
          formData.append('quantity', allItems[i].quantity);

          // Only request sections on last item (for cart drawer render)
          if (i === allItems.length - 1 && this.cart && typeof this.cart.getSectionsToRender === 'function') {
            formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id).join(','));
            formData.append('sections_url', window.location.pathname);
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.description || errData.message || `Failed to add item (${response.status})`);
          }

          lastResponse = await response.json();

          if (lastResponse.status) {
            throw new Error(lastResponse.description || lastResponse.message || 'Failed to add to cart');
          }
        }

        // Publish cart update (Dawn pub/sub)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'set-builder',
            productVariantId: this.mainVariantId,
            cartData: lastResponse,
          });
        }

        // Render cart drawer/notification (Dawn pattern)
        if (this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(lastResponse);
        } else {
          window.location = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
        }
      } catch (error) {
        console.error('SetBuilder ATC error:', error);
        const errorEl = this.querySelector('.set-builder__error');
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
          setTimeout(() => { errorEl.hidden = true; }, 5000);
        }
      } finally {
        this.addBtn.classList.remove('loading');
        this.addBtn.disabled = false;
        if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      }
    }
  }

  customElements.define('set-builder', SetBuilder);
}
